import { useMemo } from 'react';
import { Table, Currency, formatDate, formatBalance, Country } from '@ui/src';
import { columns, columnsLabels } from './variables/list';
import { PaymentType } from '../../store/types';
import { Order } from '../cash-collection/types';
import { UploadedTransactionOrdersTypes } from './types';

import cn from './styles.module.css';

const formatOrdersList = ({
  list,
  currenciesList,
  paymentTypes,
  countries,
}: {
  list: Order[];
  currenciesList?: Currency[];
  paymentTypes?: PaymentType[];
  countries?: Country[];
  showScroll?: boolean;
}): Order[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const currentCurrency = currenciesList?.find(
      (currency) => currency.id === item?.currency,
    );

    const currentPaymentType = paymentTypes?.find(
      (type) => type.paymentType === result?.paymentType,
    );

    const currentCountry = countries?.find(
      (country) => country.alpha3 === result?.originatorCountry,
    );
    const currentOriginatorsCountry = countries?.find(
      (country) => country.alpha3 === result?.originatorsCountry,
    );
    const currentOriginatorsIntermediaryBankCountry = countries?.find(
      (country) =>
        country.alpha3 === result?.originatorsIntermediaryBankCountry,
    );

    const render: Record<string, any> = {
      transferDate: (
        <div>
          {result?.transferDate
            ? formatDate(result?.transferDate || '', true)
            : '-'}
        </div>
      ),
      acceptedIn: (
        <div>
          {result?.acceptedIn
            ? formatDate(result?.acceptedIn || '', true)
            : '-'}
        </div>
      ),
      postingDate: (
        <div>
          {result?.postingDate
            ? formatDate(result?.postingDate || '', true)
            : '-'}
        </div>
      ),
      beneficiaryAccount: <div>{result?.beneficiaryAccount || '-'}</div>,
      amount: (
        <div>
          {result?.amount
            ? formatBalance(Number(result.amount), currentCurrency)
            : '-'}
        </div>
      ),
      currency: <div>{currentCurrency?.name || '-'}</div>,
      paymentType: <div>{currentPaymentType?.name || '-'}</div>,
      originatorCountry: <div>{currentCountry?.name || '-'}</div>,
      originatorCity: <div>{result?.originatorCity || '-'}</div>,
      originatorAddress: <div>{result?.originatorAddress || '-'}</div>,
      originatorsBankBic: <div>{result?.originatorsBankBic || '-'}</div>,
      originatorsBankSortCode: (
        <div>{result?.originatorsBankSortCode || '-'}</div>
      ),
      originatorsBankName: <div>{result?.originatorsBankName || '-'}</div>,
      originatorsCountry: <div>{currentOriginatorsCountry?.name || '-'}</div>,
      originatorsAddress: <div>{result?.originatorsAddress || '-'}</div>,
      originatorsIntermediaryBankBic: (
        <div>{result?.originatorsIntermediaryBankBic || '-'}</div>
      ),
      originatorsIntermediaryBankCorrespondentAccount: (
        <div>
          {result?.originatorsIntermediaryBankCorrespondentAccount || '-'}
        </div>
      ),
      originatorsIntermediaryBankName: (
        <div>{result?.originatorsIntermediaryBankName || '-'}</div>
      ),
      originatorsIntermediaryBankCountry: (
        <div>{currentOriginatorsIntermediaryBankCountry?.name || '-'}</div>
      ),
      originatorsIntermediaryBankAddress: (
        <div>{result?.originatorsIntermediaryBankAddress || '-'}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const UploadedTransactionsOrders = ({
  list,
  countries,
  currenciesList,
  paymentTypes,
  ordersErrorsList,
}: UploadedTransactionOrdersTypes) => {
  const formattedData = useMemo(() => {
    const data = formatOrdersList({
      list: list as Order[],
      currenciesList,
      paymentTypes,
      countries,
    });

    return data;
  }, [list, countries, currenciesList, paymentTypes]);

  return (
    <>
      <div className={cn.uploadedTransactionsOrdersWrapperTitle}>
        Payment orders
      </div>
      <div className={cn.uploadedTransactionsOrdersWrapper}>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          filters={{}}
          sort={{}}
          onFilterChange={() => {}}
          columnsLabels={columnsLabels}
          error={ordersErrorsList}
          tableClassName={`${!list?.length ? cn.emptyTable : ''} ${
            cn.ordersTable
          }`}
          fullWidthTable
          transactionTable
          withVerticalScroll
          withScroll
        />
      </div>
    </>
  );
};

export default UploadedTransactionsOrders;
